import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import { EditorContent, NodeViewWrapper, ReactNodeViewProps, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { aiCanvasNodeExtensions } from '../../../extensions/tiptapAiNodes';
import { gnNodeExtensions } from '../../../extensions/tiptapGnNodes';
import { getPublishedCanvas } from '../../../services/gnPublishService';
import { useGnContext } from '../GnContext';

/**
 * The tutor's PUBLISHED content rendered read-only inside the student's GN
 * (spec 046 FR-035/FR-038/FR-039, research Decision 14). This is a reference, never a
 * copy: content is fetched from the published-canvas endpoint at display time, so a
 * re-publish reaches every student without touching their documents. The change
 * highlight shows while publishedAt is newer than the student's last-seen timestamp
 * and clears once viewed.
 */
const TutorPublishedRegionNode: React.FC<ReactNodeViewProps> = ({ node }) => {
  const { courseId, nodeId } = node.attrs as { courseId: number; nodeId: number };
  const { role, lastSeenPublishedAt, markPublishedSeen, publishRefreshKey } = useGnContext();
  const [blocks, setBlocks] = useState<string | null>(null);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Published tutor content never nests another region node; filter defensively.
  const viewerExtensions = useMemo(
    () => [
      StarterKit,
      ...aiCanvasNodeExtensions,
      ...gnNodeExtensions.filter(ext => ext.name !== 'tutorPublishedRegion'),
    ],
    []
  );

  const viewer = useEditor({ extensions: viewerExtensions, editable: false, content: '' });

  useEffect(() => {
    let cancelled = false;
    if (!courseId || !nodeId || role !== 'student') {
      setLoading(false);
      return;
    }
    getPublishedCanvas(courseId, nodeId)
      .then(published => {
        if (cancelled) return;
        setBlocks(published?.blocks ?? null);
        setPublishedAt(published?.publishedAt ?? null);
      })
      .catch(() => { /* region degrades to the not-yet-published message */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [courseId, nodeId, role, publishRefreshKey]);

  useEffect(() => {
    if (!viewer || viewer.isDestroyed) return;
    if (blocks) {
      try {
        viewer.commands.setContent(JSON.parse(blocks));
      } catch {
        viewer.commands.setContent('');
      }
    }
  }, [viewer, blocks]);

  const changed = Boolean(
    publishedAt && (!lastSeenPublishedAt || new Date(publishedAt) > new Date(lastSeenPublishedAt))
  );

  // Viewing a changed region marks it seen (FR-039) — after a beat, so the highlight is noticed.
  useEffect(() => {
    if (!changed || !publishedAt) return;
    const timer = setTimeout(() => markPublishedSeen(publishedAt), 3000);
    return () => clearTimeout(timer);
  }, [changed, publishedAt, markPublishedSeen]);

  return (
    <NodeViewWrapper as="div" contentEditable={false}>
      <Box
        sx={{
          my: 1.5,
          p: 1.5,
          borderRadius: 1,
          border: '2px solid',
          borderColor: changed ? 'warning.main' : 'divider',
          bgcolor: changed ? 'warning.50' : 'background.default',
          transition: 'border-color 0.4s, background-color 0.4s',
        }}
        data-testid="gn-tutor-region"
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Chip
            icon={<SchoolIcon sx={{ fontSize: '14px !important' }} />}
            label="Tutor's content (read-only)"
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
          {changed && (
            <Chip
              icon={<FiberNewIcon sx={{ fontSize: '16px !important' }} />}
              label="Updated by your tutor"
              size="small"
              color="warning"
              sx={{ height: 22, fontSize: '0.7rem' }}
              data-testid="gn-tutor-region-changed"
            />
          )}
        </Stack>
        {loading && <CircularProgress size={20} />}
        {!loading && !blocks && (
          <Typography variant="body2" color="text.secondary">
            Your tutor hasn't published content for this topic yet.
          </Typography>
        )}
        {!loading && blocks && (
          <Box sx={{ '& .ProseMirror': { outline: 'none' } }}>
            <EditorContent editor={viewer} />
          </Box>
        )}
      </Box>
    </NodeViewWrapper>
  );
};

export default TutorPublishedRegionNode;
